class CustomSizing {
  constructor(container) {
    this.container = container;
    this.sizingId = this.container.dataset.sizingId;
    this.productId = this.container.dataset.productId;
    this.sectionId = this.container.dataset.sectionId;

    // Define product configurations
    this.productConfigs = {
      // Configuration for fitted sheets only + round set
      fittedSheets: {
        productIds: ['8722898583764', '8722898583765'],
        ranges: [
          { min: 20, max: 30, variant: '20-30' },
          { min: 31, max: 41, variant: '31-41' },
          { min: 42, max: 47, variant: '42-47' },
          { min: 48, max: 66, variant: '48-66' },
          { min: 67, max: 76, variant: '67-76' },
          { min: 77, max: 80, variant: '77-80' }
        ]
      },
      fittedSheetSet: {
        productIds: ['8722898616532'],
        ranges: [
          { min: 20, max: 38, variant: '20-38' },
          { min: 39, max: 47, variant: '39-47' },
          { min: 48, max: 60, variant: '48-60' },
          { min: 61, max: 76, variant: '61-76' },
          { min: 77, max: 80, variant: '77-80' }
        ]
      },
      customComforter: {
        productIds: ['8722897436884'],
        ranges: [
          { min: 25, max: 39, variant: '25-39' },
          { min: 40, max: 64, variant: '40-64' },
          { min: 65, max: 79, variant: '65-79' },
          { min: 80, max: 100, variant: '80-100' }
        ]
      },
      oversizeSheets: {
        productIds: ['8723357892820'],
        ranges: [
          { name: 'Wyoming King 84" x 84"', variant: 'Wyoming King 84" x 84"' },
          { name: 'Alberta King 96" x 96"', variant: 'Alberta King 96" x 96"' },
          { name: 'Family Bed 120" x 80"', variant: 'Family Bed 120" x 80"' },
          { name: 'Family XL 144" x 84"', variant: 'Family XL 144" x 84"' },
          { name: 'Texas King 80" x 98"', variant: 'Texas King 80" x 98"' }
        ],
        isNamedVariant: true
      },
      roundSheets: {
        productIds: ['8722897535188'],
        ranges: [
          { min: 60, max: 60, variant: '60' },
          { min: 76, max: 76, variant: '76' },
          { min: 80, max: 80, variant: '80' },
          { min: 84, max: 84, variant: '84' },
          { min: 87, max: 87, variant: '87' },
          { min: 96, max: 96, variant: '96' },
          { min: 99, max: 99, variant: '99' },
          { min: 108, max: 108, variant: '108' }
        ],
        formatVariant: (value) => `${value}`
      }
    };

    this.widthRanges = this.getProductRanges();
    this.initializeEventListeners();
  }

  getProductRanges() {
    const config = Object.values(this.productConfigs).find(config => 
      config.productIds.includes(this.productId)
    );

    if (!config) {
      console.warn(`No specific ranges found for product ${this.productId}, using fitted sheets ranges as default`);
      return this.productConfigs.fittedSheets.ranges;
    }
    return config.ranges;
  }

  getProductConfig() {
    return Object.values(this.productConfigs).find(config => 
      config.productIds.includes(this.productId)
    );
  }

  initializeEventListeners() {
    if (this.sizingId === 'custom_size_width') {
      const select = this.container.querySelector('[data-custom-size-select]');
      console.log('Found width select element:', !!select);

      if (select) {
        select.addEventListener('change', (e) => {
          const value = e.target.value;
          console.log('Width select changed:', value);
          this.handleWidthChange(value);
        });
      }
    }
  }
  
  formatDimension(value) {
    // Handle cases like "87-round" or numeric dimensions
    value = value.toString().toLowerCase();
    
    // Handle round dimensions
    if (value.includes('round')) {
      // Extract the number and add quotes
      const number = value.match(/\d+/)[0];
      return `${number}" Round`;
    }
    
    // Handle regular numeric dimensions
    if (!isNaN(value)) {
      return `${value}"`;
    }
    
    return value;
  }

  convertKebabToVariantName(kebabStr) {
    if (!kebabStr) return '';
    
    // Special handling for round sizes
    if (kebabStr.toLowerCase().includes('round')) {
      return this.formatDimension(kebabStr);
    }
    
    // Convert kebab case to proper variant name format
    // e.g., 'family-xl-144-x-84' -> 'Family XL 144" x 84"'
    return kebabStr
      .split('-')
      .map((part, index) => {
        // Special handling for XL
        if (part.toLowerCase() === 'xl') return 'XL';
        // Special handling for x (dimension separator)
        if (part.toLowerCase() === 'x') return 'x';
        // For numeric values, add inch mark
        if (!isNaN(part)) return `${part}"`;
        // Capitalize first letter of each word, except x
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ')
      // Add inch marks to dimensions if not already added
      .replace(/(\d+)(?!["]) x (\d+)(?![")])/, '$1" x $2"');
  }

  getVariantRange(width) {
    console.log('Getting variant range for width:', width);
    const config = this.getProductConfig();
    
    // Handle named variants for oversize sheets
    if (config && config.isNamedVariant) {
      // Convert kebab-case to proper variant name
      const variantName = this.convertKebabToVariantName(width);
      console.log('Converted variant name:', variantName);
      
      // Try to find an exact match first
      let range = this.widthRanges.find(range => range.variant === variantName);
      
      // If no exact match, try finding a match ignoring case and whitespace
      if (!range) {
        const normalizedInput = variantName.toLowerCase().replace(/\s+/g, '');
        range = this.widthRanges.find(range => {
          const normalizedVariant = range.variant.toLowerCase().replace(/\s+/g, '');
          return normalizedVariant === normalizedInput;
        });
      }
      
      if (range) {
        return range.variant;
      }
    }
    
    // If not a named variant or no match found, try numeric ranges
    // First check if it's a simple number (for round sheets)
    if (!isNaN(width) && config && config.formatVariant) {
      const range = this.widthRanges.find(range => 
        range.min === parseInt(width) && range.max === parseInt(width)
      );
      if (range) {
        return range.variant;
      }
    }
    
    // Finally, try numeric range matching
    const numericWidth = parseInt(width.replace(/[^0-9]/g, ''));
    console.log('Numeric width:', numericWidth);
    
    if (!isNaN(numericWidth)) {
      const range = this.widthRanges.find(range => 
        range.min && range.max && 
        numericWidth >= range.min && 
        numericWidth <= range.max
      );
      
      console.log('Found range:', range);
      return range ? range.variant : null;
    }
    
    return null;
  }

  handleWidthChange(value) {
    console.log('Handling width change:', value);
    const variantRange = this.getVariantRange(value);
    
    if (!variantRange) {
        console.warn('No matching variant range found for width:', value);
        return;
    }

    console.log('Found variant range:', variantRange);

    // Look for select element with data-variant-input attribute
    const sizeSelect = document.querySelector('select[data-variant-input]');
    
    console.log('Found size select:', !!sizeSelect);

    if (sizeSelect) {
        // Find the matching option and set it as selected
        const options = Array.from(sizeSelect.options);
        const matchingOption = options.find(option => option.value === variantRange);

        if (matchingOption) {
            // Set the new value
            sizeSelect.value = variantRange;
            
            console.log('Updated select value:', {
                value: sizeSelect.value,
                selectedOption: matchingOption.text
            });
            
            // Trigger change event
            sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    // Store the width as a property
    this.updateProperty(value);
}
  
  updateProperty(value) {
    console.log('Updating property:', value);
    const config = this.getProductConfig();
    
    let formattedValue = value;
    
    if (config && config.isNamedVariant) {
      // Use the same conversion logic as variant matching
      formattedValue = this.convertKebabToVariantName(value);
    } else if (config && config.formatVariant) {
      formattedValue = config.formatVariant(value);
    }

    let hiddenInput = document.querySelector(`[name="properties[${this.sizingId}]"]`);
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `properties[${this.sizingId}]`;
      this.container.appendChild(hiddenInput);
    }
    hiddenInput.value = formattedValue;
    console.log('Updated hidden input:', {
      name: hiddenInput.name,
      value: hiddenInput.value
    });
  }
}

// Initialize only on width sizing container
window.addEventListener('DOMContentLoaded', () => {
  console.log('DOM Content Loaded - initializing CustomSizing');
  const widthContainer = document.querySelector('[data-custom-sizing][data-sizing-id="custom_size_width"]');
  if (widthContainer) {
    new CustomSizing(widthContainer);
  }
});